import { ClickAwayListener, Popover } from '@mui/material';

/**
 * The popover every condition panel opens in. It is deliberately not modal: the surface behind keeps its mouse
 * events, so the page still scrolls and hovers and a click on another filter tag reaches that tag instead of being
 * swallowed. Nothing in front is left to catch that click, so closing runs off ClickAwayListener — it listens on the
 * document, which both lets the click through and, unlike a DOM-only check, still counts the portaled menus a panel
 * opens (operator select, sub-panels) as clicks inside.
 */
export const ConditionPanelPopover = ({
  open,
  anchorEl,
  onClose,
  children,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  children: React.ReactElement;
}) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    hideBackdrop
    disableScrollLock
    // Without this the focus trap pulls focus back out of whatever the user clicks behind the panel.
    disableEnforceFocus
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    // Dropping the backdrop is not enough: the modal root still spans the viewport, so it has to pass
    // pointer events through and let the paper alone take them back.
    sx={{ pointerEvents: 'none' }}
    slotProps={{ paper: { sx: { marginTop: '4px', pointerEvents: 'auto' } } }}
  >
    <ClickAwayListener
      // The press, not the click: a click is dispatched on the nearest common ancestor of where the button went down
      // and came back up, so pressing a select inside the panel and releasing over its portaled menu reports `body`
      // as the target — outside the panel by every check, which would dismiss it mid-edit.
      mouseEvent='onMouseDown'
      touchEvent='onTouchStart'
      onClickAway={(event) => {
        // The trigger owns the open/closed toggle. Closing on its press as well would fight it, since the click that
        // follows would find the panel closed and open it straight back up.
        if (anchorEl?.contains(event.target as Node)) {
          return;
        }
        onClose();
      }}
    >
      {children}
    </ClickAwayListener>
  </Popover>
);
